@use 'sass:string';
@use 'src/globals/main';

pn-button {
  display: inline-block;
}

pn-button .pn-button {
  position: relative;
  cursor: pointer;

  padding: 0.75em 1.5em;
  width: string.unquote('round(up, 100%, 1px)');
  min-height: 3em;
  min-width: 5.5em;

  border: none;
  border-radius: 3em;

  font-family: inherit;
  font-size: 1em;
  font-weight: 500;

  color: main.$white;
  background-color: transparent;

  display: flex;
  justify-content: center;
  align-items: center;

  text-decoration: none;
  line-height: 1.5em;
  -webkit-tap-highlight-color: transparent;

  @include main.pn-transition('color, outline-color');
  @include main.pn-focus();

  &:disabled,
  &[aria-disabled] {
    cursor: auto;
    pointer-events: none;
  }

  &-content {
    z-index: 2;
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 0.5em;

    @include main.pn-transition('opacity, transform');
    transition-delay: main.$timing;
  }

  // This is a separate element because it has to be a leaf node to avoid bogging down performance when animating
  &-bg {
    position: absolute;
    width: 100%;
    height: 100%;
    top: 0;
    left: 50%;
    transform: translateX(-50%) translateY(0);
    border-radius: 3em;
    border: none;
    background-color: main.$blue700;
    overflow: hidden;

    @include main.pn-transition('width, background-color, border-color');
    transition-delay: main.$timing, 0s, 0s;

    // Hover element (sliding background)
    &::after {
      content: '';
      position: absolute;
      will-change: transform;
      width: 100%;
      height: 100%;
      background-color: main.$blue900;
      border-radius: inherit;
      left: 0;
      transform: translateX(-103%);
      opacity: 0.5;
      @include main.pn-transition('transform');
    }
  }

  @include main.pn-ripple(main.$white);

  &-tooltip {
    z-index: 5;
    position: absolute;
    top: calc(100% + 0.35em);
    left: 50%;
    pointer-events: none;

    color: main.$white;
    background-color: main.$blue900;
    box-shadow:
      0 0.075em 0.225em rgba(0, 0, 0, 0.1),
      0 0.4em 0.9em rgba(0, 0, 0, 0.13);

    padding: 0.25em 0.75em;
    border-radius: inherit;

    max-width: min(95vw, 40em);
    transform: translateX(-50%) translateX(var(--pn-dx));
    width: max-content;

    opacity: 0;
    @include main.pn-transition('opacity');

    &-text {
      font-size: 0.875em;
      font-weight: 300;
    }
  }

  pn-icon {
    flex-shrink: 0;
    .pn-icon-svg path {
      fill: main.$white;
    }
  }

  pn-spinner {
    position: absolute;
    transform: scale(0);
  }

  &:hover,
  &:focus {
    .pn-button-bg:after {
      transform: translateX(0);
    }
  }

  &[data-appearance='light'] {
    color: main.$blue700;

    pn-icon .pn-icon-svg path {
      fill: main.$blue700;
    }

    pn-spinner {
      .pn-spinner-circle {
        stroke: main.$blue700;
      }
      .pn-spinner-dot {
        fill: main.$blue700;
      }
    }

    .pn-button-bg {
      border-color: main.$blue700;
      background-color: main.$white;

      &:after {
        background-color: main.$blue50;
        opacity: 1;
      }

      .pn-ripple {
        background-color: main.$blue700;
      }
    }
  }

  &[data-appearance='warning'] {
    color: main.$warning;

    pn-icon .pn-icon-svg path {
      fill: main.$warning;
    }

    pn-spinner {
      .pn-spinner-circle {
        stroke: main.$warning;
      }
      .pn-spinner-dot {
        fill: main.$warning;
      }
    }

    .pn-button-bg {
      border: 0.0625em solid main.$warning;
      background-color: main.$coral50;

      &:after {
        background-color: main.$warning;
        opacity: 1;
      }
    }

    &:hover,
    &:focus {
      color: main.$white;
      pn-icon .pn-icon-svg path {
        fill: main.$white;
      }

      pn-spinner {
        .pn-spinner-circle {
          stroke: main.$white;
        }
        .pn-spinner-dot {
          fill: main.$white;
        }
      }
    }

    @include main.pn-focus(main.$warning);
  }

  &[data-variant='borderless'] {
    .pn-button-bg {
      border: none;
      background-color: transparent;
    }

    .pn-button-bg:after {
      background-color: main.$white;
      opacity: 0.1;
    }
    &[data-appearance='light'] {
      .pn-button-bg:after {
        background-color: main.$blue50;
        opacity: 1;
      }
    }
    &[data-appearance='warning'] {
      .pn-button-bg:after {
        background-color: main.$warning;
        opacity: 1;
      }
    }
  }

  &[data-variant='outlined'] {
    .pn-button-bg {
      border: 0.0625em solid main.$white;
      background-color: transparent;
      &:after {
        background-color: main.$white;
        opacity: 0.1;
      }
    }

    &[data-appearance='light'] .pn-button-bg {
      border-color: main.$blue700;
      background-color: main.$white;
      &:after {
        background-color: main.$blue400;
        opacity: 0.1;
      }
    }

    &[data-appearance='warning'] .pn-button-bg {
      border-color: main.$warning;
      &:after {
        background-color: main.$warning;
        opacity: 1;
      }
    }
  }

  &[data-left-icon] .pn-button-content {
    flex-direction: row-reverse;
  }

  &[data-icon-only] {
    width: 3em;
    min-width: 0;

    &[data-small] {
      width: 2em;
      min-width: 0;
      padding: 0;
    }
  }

  &[data-small] {
    padding: 0.375em 1em;
    min-height: 2em;
    min-width: unset;
    line-height: 1.25em;

    .pn-button-content {
      gap: 0.25em;
    }

    .pn-button-text {
      font-size: 0.875em;
    }

    pn-icon .pn-icon-svg {
      height: 1.25em;
      width: 1.25em;
    }
  }

  &[data-tooltip-open] {
    .pn-button-tooltip {
      opacity: 1;
    }
  }

  &[data-tooltip-upwards] {
    .pn-button-tooltip {
      top: auto;
      bottom: calc(100% + 0.35em);
    }
  }

  &[data-loading] {
    pn-spinner {
      transform: scale(1);
      @include main.pn-transition('transform');
      transition-delay: main.$timing;
    }

    .pn-button-content {
      pointer-events: none;
      opacity: 0;
      transition-delay: 0s;
      transform: translateY(100%);
    }

    .pn-button-bg {
      width: 3em;
    }

    &[data-small] .pn-button-bg {
      width: 2em;
    }
  }
}
